import { ApolloError, useApolloClient, useLazyQuery, useQuery, QueryResult } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  GET_COMPANY_V2,
  GET_COMPANIES_SUGGESTIONS,
  COMPANY_SUGGESTION_FRAGMENT
} from 'features/Companies/queries-legacy'
import { GTCompanySuggestion, GTCompanySuggestionsResponse, GTCompanyV2 } from 'gql/types.generated'

export const useCompany = (companyId: string): QueryResult<{ companyV2: GTCompanyV2 }> => {
  return useQuery<{ companyV2: GTCompanyV2 }>(GET_COMPANY_V2, { variables: { companyId }, skip: !companyId })
}

interface IUseCompanyAutocompleteOptions {
  skip?: boolean
}

export const useCompanyAutocomplete = (
  companyId: string,
  { skip }: IUseCompanyAutocompleteOptions = {}
): GTCompanySuggestion | null => {
  const { data } = useQuery<{ companySuggestions: GTCompanySuggestionsResponse }>(GET_COMPANIES_SUGGESTIONS, {
    skip,
    variables: {
      input: {
        id: companyId
      }
    }
  })

  return data?.companySuggestions?.items.at(0) || null
}

interface IUseCompanySuggestionsByIdsQueryOptions {
  ids: string[]
  skip?: boolean
}

interface IUseCompanySuggestionsByIdsQueryResponse {
  companies: GTCompanySuggestion[]
  loading?: boolean
  error?: ApolloError
}

export const useCompanySuggestionsByIdsQuery = ({
  ids,
  skip
}: IUseCompanySuggestionsByIdsQueryOptions): IUseCompanySuggestionsByIdsQueryResponse => {
  const client = useApolloClient()
  const [companies, setCompanies] = useState<GTCompanySuggestion[]>([])
  const [getCompanies, { loading, error }] = useLazyQuery<{ companySuggestions: GTCompanySuggestionsResponse }>(
    GET_COMPANIES_SUGGESTIONS,
    {
      variables: {
        input: {
          ids
        }
      }
    }
  )

  const findCompanies = async (ids: string[]): Promise<void> => {
    const cachedUsers = ids.map((id) =>
      client.readFragment<GTCompanySuggestion>({ id: `CompanySuggestion:${id}`, fragment: COMPANY_SUGGESTION_FRAGMENT })
    )
    if (cachedUsers.every((user) => !!user)) {
      setCompanies(cachedUsers as GTCompanySuggestion[])
    } else {
      const companiesResponse = await getCompanies({
        variables: {
          input: {
            ids
          }
        }
      })
      setCompanies(companiesResponse.data?.companySuggestions.items || [])
    }
  }

  useEffect(() => {
    if (!skip) {
      findCompanies(ids)
    }
  }, [ids.toString(), skip])

  return {
    companies,
    loading,
    error
  }
}
